import useGetApi from '../useGetApi';
import { RebateTierProximity } from '@erp/shared-types';
import { type DataSourceType } from '@/components/common/DataSourceSelect';

interface UseRebateTierProximityParams {
  year?: number;
  thresholdTypeFilter?: string[]; // ['growth', 'volume']
  retroactiveFilter?: boolean[]; // [true, false]
  includeMissingBaseline?: boolean; // Include rules with missing growth baseline
  divisionFilter?: string[]; // Division IDs to filter by (empty = all)
  dataSource?: DataSourceType; // 'estimates', 'reported', 'predictions' (default: 'predictions')
}

export function useRebateTierProximity(params?: UseRebateTierProximityParams) {
  // Default to current UTC year if not provided
  const year = params?.year ?? new Date().getUTCFullYear();

  // Default filters: growth + retroactive, hide missing baseline
  const thresholdTypeFilter = params?.thresholdTypeFilter ?? ['growth'];
  const retroactiveFilter = params?.retroactiveFilter ?? [true];
  const includeMissingBaseline = params?.includeMissingBaseline ?? false;
  const divisionFilter = params?.divisionFilter ?? [];
  const dataSource = params?.dataSource ?? 'predictions';

  // Build query parameters
  const queryParameters: Record<string, string | number> = {
    year,
    dataSource,
  };

  // Add thresholdType filter if provided (comma-separated)
  if (thresholdTypeFilter.length > 0) {
    queryParameters.thresholdType = thresholdTypeFilter.join(',');
  }

  // Add retroactive filter if provided (comma-separated: 'true', 'false')
  if (retroactiveFilter.length > 0) {
    queryParameters.retroactive = retroactiveFilter
      .map((b) => String(b))
      .join(',');
  }

  // Add includeMissingBaseline filter
  if (includeMissingBaseline) {
    queryParameters.includeMissingBaseline = 'true';
  }

  // Add divisions filter if provided (comma-separated division IDs)
  // Empty array = show all divisions (don't include in query)
  if (divisionFilter.length > 0) {
    queryParameters.divisions = divisionFilter.join(',');
  }

  return useGetApi<RebateTierProximity[], RebateTierProximity[]>({
    endpoint: 'rebates/tier-proximity',
    queryParameters,
    transformResponse: (data: RebateTierProximity[]) => {
      // Backend returns { data: RebateTierProximity[] }
      // useGetApi unwraps ApiResponse wrapper, so data is RebateTierProximity[] directly
      // Return the array, with fallback to empty array to ensure we never return undefined
      return data || [];
    },
    options: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  });
}
